import type { FC, ReactNode } from 'react'

import { Check, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'
import { SectionHeading } from '@/components/landing/section-heading'

const rows = [
    {
        label: 'Juros',
        consortium: 'Não tem juros',
        financing: 'Juros mensais sobre o saldo devedor',
        isTradeOff: false
    },
    {
        label: 'Custo do crédito',
        consortium: 'Taxa de administração diluída no prazo',
        financing: 'Juros, tarifas, seguros e IOF',
        isTradeOff: false
    },
    {
        label: 'Entrada',
        consortium: 'Não exige entrada',
        financing: 'Geralmente exigida',
        isTradeOff: false
    },
    {
        label: 'Na hora da compra',
        consortium: 'Você paga à vista e negocia desconto',
        financing: 'A compra é financiada pelo banco',
        isTradeOff: false
    },
    {
        label: 'Quando recebe o crédito',
        consortium: 'Na contemplação, por sorteio ou lance',
        financing: 'Logo após a aprovação',
        isTradeOff: true
    },
    {
        label: 'Ideal para',
        consortium: 'Quem pode planejar a conquista',
        financing: 'Quem precisa do bem imediatamente',
        isTradeOff: false
    }
]

export const ComparisonSection: FC = (): ReactNode => {
    return (
        <section
            id='comparativo'
            aria-labelledby='comparativo-titulo'
            className='border-y border-border bg-card/60 py-24 sm:py-32'
        >
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='comparativo-titulo'
                    eyebrow='Consórcio ou financiamento'
                    title='Planejar custa menos do que ter pressa'
                    description='No financiamento você recebe o bem agora e paga juros por isso. No consórcio você planeja, paga só a taxa de administração e compra à vista quando for contemplado.'
                />

                <div className='mt-14 hidden md:block'>
                    <table className='w-full border-separate border-spacing-0 text-left'>
                        <caption className='sr-only'>
                            Comparação entre consórcio e financiamento
                        </caption>

                        <thead>
                            <tr>
                                <th scope='col' className='w-[26%] pb-6 pr-6 align-bottom'>
                                    <span className='sr-only'>Critério</span>
                                </th>

                                <th scope='col' className='rounded-t-[1.75rem] bg-ink px-7 pb-6 pt-7 align-bottom text-primary-foreground'>
                                    <span className='flex items-center gap-3'>
                                        <LogoMark className='size-9 rounded-[9px] ring-1 ring-gold/30' />

                                        <span className='font-heading text-3xl font-normal'>
                                            Consórcio
                                        </span>
                                    </span>
                                </th>

                                <th scope='col' className='px-7 pb-6 pt-7 align-bottom font-heading text-3xl font-normal text-foreground/60'>
                                    Financiamento
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((row, index) => {
                                const isLast = index === rows.length - 1

                                return (
                                    <tr key={row.label}>
                                        <th scope='row' className='border-t border-border py-5 pr-6 align-top text-sm font-bold text-foreground'>
                                            {row.label}
                                        </th>

                                        <td
                                            className={cn(
                                                'border-t border-ink-line bg-ink px-7 py-5 align-top text-primary-foreground',
                                                isLast && 'rounded-b-[1.75rem] pb-7'
                                            )}
                                        >
                                            <span className='flex items-start gap-3 font-semibold'>
                                                {row.isTradeOff ? (
                                                    <Clock aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-primary-foreground/60' />
                                                ) : (
                                                    <Check aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-gold' />
                                                )}

                                                {row.consortium}
                                            </span>
                                        </td>

                                        <td className='border-t border-border px-7 py-5 align-top text-muted-foreground'>
                                            {row.financing}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                <ul className='mt-12 grid gap-4 md:hidden'>
                    {rows.map(row => (
                        <li key={row.label} className='overflow-hidden rounded-2xl bg-card ring-1 ring-border'>
                            <p className='px-5 pt-5 text-xs font-bold uppercase tracking-[0.16em] text-bronze'>
                                {row.label}
                            </p>

                            <dl className='mt-3 grid grid-cols-2'>
                                <div className='bg-ink px-5 py-4 text-primary-foreground'>
                                    <dt className='text-xs font-semibold text-primary-foreground/60'>
                                        Consórcio
                                    </dt>

                                    <dd className='mt-1 text-sm font-semibold'>
                                        {row.consortium}
                                    </dd>
                                </div>

                                <div className='px-5 py-4'>
                                    <dt className='text-xs font-semibold text-muted-foreground'>
                                        Financiamento
                                    </dt>

                                    <dd className='mt-1 text-sm text-foreground/80'>
                                        {row.financing}
                                    </dd>
                                </div>
                            </dl>
                        </li>
                    ))}
                </ul>

                <p className='mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground'>
                    O consórcio não substitui o financiamento em todos os casos. Se você precisa do bem agora, o financiamento pode ser o caminho. Se pode planejar, o consórcio costuma custar bem menos no total.
                </p>
            </div>
        </section>
    )
}
